import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IdCardIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo, useState } from "react";
import { NATIVE_TOKEN_ADDRESS } from "../../../../../../constants/addresses.js";
import { isSwapRequiredPostOnramp } from "../../../../../../pay/buyWithFiat/isSwapRequiredPostOnramp.js";
import { formatNumber } from "../../../../../../utils/formatNumber.js";
import { iconSize, spacing, } from "../../../../../core/design-system/index.js";
import { useWalletBalance } from "../../../../../core/hooks/others/useWalletBalance.js";
import { useBuyWithCryptoQuote } from "../../../../../core/hooks/pay/useBuyWithCryptoQuote.js";
import { useBuyWithFiatQuote } from "../../../../../core/hooks/pay/useBuyWithFiatQuote.js";
import { useActiveAccount } from "../../../../../core/hooks/wallets/useActiveAccount.js";
import { LoadingScreen } from "../../../../wallets/shared/LoadingScreen.js";
import { ChainName } from "../../../components/ChainName.js";
import { Drawer, DrawerOverlay, useDrawer, } from "../../../components/Drawer.js";
import { Spacer } from "../../../components/Spacer.js";
import { Spinner } from "../../../components/Spinner.js";
import { SwitchNetworkButton } from "../../../components/SwitchNetwork.js";
import { Container, Line, ModalHeader } from "../../../components/basic.js";
import { Button } from "../../../components/buttons.js";
import { Text } from "../../../components/text.js";
import { TokenSymbol } from "../../../components/token/TokenSymbol.js";
import { ConnectButton } from "../../ConnectButton.js";
import { ChainButton, NetworkSelectorContent } from "../../NetworkSelector.js";
import { CoinsIcon } from "../../icons/CoinsIcon.js";
import { TokenSelector } from "../TokenSelector.js";
import { WalletSwitcherConnectionScreen } from "../WalletSwitcherConnectionScreen.js";
import { isNativeToken } from "../nativeToken.js";
import { DirectPaymentModeScreen } from "./DirectPaymentModeScreen.js";
import { EstimatedTimeAndFees } from "./EstimatedTimeAndFees.js";
import { PayTokenIcon } from "./PayTokenIcon.js";
import { PayWithCreditCard } from "./PayWIthCreditCard.js";
import { TransactionModeScreen } from "./TransactionModeScreen.js";
import { WalletSelectorButton } from "./WalletSelectorButton.js";
import { CurrencySelection } from "./fiat/CurrencySelection.js";
import { FiatFlow } from "./fiat/FiatFlow.js";
import { useEnabledPaymentMethods, } from "./main/useEnabledPaymentMethods.js";
import { useFiatCurrencySelectionStates, useFromTokenSelectionStates, useToTokenSelectionStates, } from "./main/useUISelectionStates.js";
import { openOnrampPopup } from "./openOnRamppopup.js";
import { BuyTokenInput } from "./swap/BuyTokenInput.js";
import { FiatFees, SwapFees } from "./swap/Fees.js";
import { PayWithCrypto } from "./swap/PayWithCrypto.js";
import { SwapFlow } from "./swap/SwapFlow.js";
import { TransferFlow } from "./swap/TransferFlow.js";
import { WalletSwitcherDrawerContent } from "./swap/WalletSwitcherDrawerContent.js";
import { addPendingTx } from "./swap/pendingSwapTx.js";
import { useBuySupportedDestinations, useBuySupportedSources, } from "./swap/useSwapSupportedChains.js";
import { usePayerSetup } from "./usePayerSetup.js";
/**
 * @internal
 */
export default function BuyScreen(props) {
    const isTestMode = props.payOptions.buyWithCrypto
        ? props.payOptions.buyWithCrypto.testMode
        : undefined;
    const supportedDestinationsQuery = useBuySupportedDestinations(props.client, isTestMode);
    if (!supportedDestinationsQuery.data) {
        return _jsx(LoadingScreen, {});
    }
    return (_jsx(BuyScreenContent, { ...props, supportedDestinations: supportedDestinationsQuery.data }));
}
/**
 * @internal
 */
function BuyScreenContent(props) {
    const { client, supportedDestinations, connectLocale, payOptions } = props;
    const activeAccount = useActiveAccount();
    const { payer, setPayer } = usePayerSetup();
    const [screen, setScreen] = useState({
        id: "main",
    });
    const { tokenAmount, setTokenAmount, toChain, setToChain, deferredTokenAmount, toToken, setToToken, } = useToTokenSelectionStates({
        payOptions,
        supportedDestinations,
    });
    const [hasEditedAmount, setHasEditedAmount] = useState(false);
    const onDone = useCallback(() => {
        setScreen({ id: "main" });
        props.onDone();
    }, [props.onDone]);
    // check if the screen is expanded or not
    // update supportedSources whenever toToken or toChain is updated
    const supportedSourcesQuery = useBuySupportedSources({
        client: props.client,
        destinationChainId: toChain.id,
        destinationTokenAddress: isNativeToken(toToken)
            ? NATIVE_TOKEN_ADDRESS
            : toToken.address,
    });
    const destinationSupportedTokens = useMemo(() => {
        return createSupportedTokens(supportedDestinations, payOptions, props.supportedTokens);
    }, [props.supportedTokens, supportedDestinations, payOptions]);
    const sourceSupportedTokens = useMemo(() => {
        if (!supportedSourcesQuery.data) {
            return undefined;
        }
        const supportedSources = supportedSourcesQuery.data;
        return createSupportedTokens(supportedSources, payOptions, props.supportedTokens);
    }, [props.supportedTokens, supportedSourcesQuery.data, payOptions]);
    const { fromChain, setFromChain, fromToken, setFromToken } = useFromTokenSelectionStates({
        payOptions,
        supportedSources: supportedSourcesQuery.data || [],
    });
    const { selectedCurrency, setSelectedCurrency } = useFiatCurrencySelectionStates({
        payOptions,
    });
    const enabledPaymentMethods = useEnabledPaymentMethods({
        payOptions: props.payOptions,
        supportedDestinations: props.supportedDestinations,
        toChain: toChain,
        toToken: toToken,
    });
    const payDisabled = enabledPaymentMethods.showPaymentSelection === false &&
        enabledPaymentMethods.buyWithCryptoEnabled === false &&
        enabledPaymentMethods.buyWithFiatEnabled === false;
    // screens ----------------------------
    const onSwapSuccess = useCallback((_status) => {
        props.payOptions.onPurchaseSuccess?.({
            type: "crypto",
            status: _status,
        });
    }, [props.payOptions.onPurchaseSuccess]);
    const onFiatSuccess = useCallback((_status) => {
        props.payOptions.onPurchaseSuccess?.({
            type: "fiat",
            status: _status,
        });
    }, [props.payOptions.onPurchaseSuccess]);
    if (screen.id === "connect-payer-wallet") {
        return (_jsx(WalletSwitcherConnectionScreen, { accountAbstraction: props.connectOptions?.accountAbstraction, appMetadata: props.connectOptions?.appMetadata, chain: props.connectOptions?.chain, chains: props.connectOptions?.chains, client: props.client, connectLocale: props.connectLocale, isEmbed: props.isEmbed, onBack: () => setScreen(screen.backScreen), onSelect: (w) => {
                const account = w.getAccount();
                const chain = w.getChain();
                if (w && account && chain) {
                    setPayer({
                        account,
                        chain,
                        wallet: w,
                    });
                }
            }, recommendedWallets: props.connectOptions?.recommendedWallets, showAllWallets: props.connectOptions?.showAllWallets === undefined
                ? true
                : props.connectOptions?.showAllWallets, walletConnect: props.connectOptions?.walletConnect, wallets: props.connectOptions?.wallets?.filter((w) => w.id !== "inApp") }));
    }
    if (screen.id === "swap-flow" && payer) {
        return (_jsx(SwapFlow, { title: props.title, transactionMode: payOptions.mode === "transaction", isEmbed: props.isEmbed, client: client, onBack: () => {
                setScreen({
                    id: "buy-with-crypto",
                });
            }, buyWithCryptoQuote: screen.quote, payer: payer, isFiatFlow: false, onDone: onDone, onTryAgain: () => {
                setScreen({
                    id: "buy-with-crypto",
                });
            }, onSuccess: onSwapSuccess }));
    }
    if (screen.id === "fiat-flow" && payer) {
        return (_jsx(FiatFlow, { title: props.title, transactionMode: payOptions.mode === "transaction", quote: screen.quote, onBack: () => {
                setScreen({
                    id: "buy-with-fiat",
                });
            }, client: client, testMode: props.payOptions.buyWithFiat !== false &&
                props.payOptions.buyWithFiat?.testMode === true, theme: typeof props.theme === "string" ? props.theme : props.theme.type, openedWindow: screen.openedWindow, onDone: onDone, isEmbed: props.isEmbed, payer: payer, onSuccess: onFiatSuccess }));
    }
    if (screen.id === "transfer-flow" && payer && activeAccount) {
        const goBack = () => setScreen({ id: "buy-with-crypto" });
        // TODO (pay) pass it via screen props
        const defaultRecipientAddress = props.payOptions?.paymentInfo?.sellerAddress;
        const receiverAddress = defaultRecipientAddress || activeAccount.address;
        return (_jsx(TransferFlow, { title: props.title, onBack: goBack, payer: payer, client: props.client, chain: toChain, token: toToken, tokenAmount: tokenAmount, receiverAddress: receiverAddress, transactionMode: props.payOptions.mode === "transaction", isEmbed: props.isEmbed, onDone: onDone, onTryAgain: () => {
                setScreen({
                    id: "buy-with-crypto",
                });
            }, onSuccess: onSwapSuccess }));
    }
    if (screen.id === "select-currency") {
        const goBack = () => setScreen(screen.backScreen);
        return (_jsx(CurrencySelection, { onSelect: (currency) => {
                goBack();
                setSelectedCurrency(currency);
            }, onBack: goBack }));
    }
    if (screen.id === "select-to-token") {
        const chains = supportedDestinations.map((x) => x.chain);
        const goBack = () => setScreen(screen.backScreen);
        const allowEdits = payOptions?.prefillBuy
            ?.allowEdits;
        // if token selection is disabled - only show network selector screen
        if (allowEdits?.token === false) {
            return (_jsx(ChainSelectionScreen, { chains: chains, client: props.client, connectLocale: props.connectLocale, setChain: setToChain, goBack: goBack }));
        }
        return (_jsx(TokenSelector, { onBack: goBack, tokenList: ((toChain?.id ? destinationSupportedTokens[toChain.id] : undefined) ||
                []).filter((x) => x.address !== NATIVE_TOKEN_ADDRESS), onTokenSelect: (tokenInfo) => {
                setToToken(tokenInfo);
                goBack();
            }, chain: toChain, chainSelection: 
            // hide chain selection if it's disabled
            allowEdits?.chain !== false
                ? {
                    chains: chains,
                    select: (c) => {
                        setToChain(c);
                    },
                }
                : undefined, connectLocale: connectLocale, client: client, modalTitle: props.title }));
    }
    if (screen.id === "select-from-token" &&
        supportedSourcesQuery.data &&
        sourceSupportedTokens) {
        const chains = supportedSourcesQuery.data.map((x) => x.chain);
        const goBack = () => setScreen(screen.backScreen);
        // if token selection is disabled - only show network selector screen
        if (payOptions.buyWithCrypto !== false &&
            payOptions.buyWithCrypto?.prefillSource?.allowEdits?.token === false) {
            return (_jsx(ChainSelectionScreen, { chains: chains, client: props.client, connectLocale: props.connectLocale, setChain: setFromChain, goBack: goBack }));
        }
        return (_jsx(TokenSelector, { onBack: goBack, tokenList: ((fromChain?.id ? sourceSupportedTokens[fromChain.id] : undefined) ||
                []).filter((x) => x.address !== NATIVE_TOKEN_ADDRESS), onTokenSelect: (tokenInfo) => {
                setFromToken(tokenInfo);
                goBack();
            }, chain: fromChain, chainSelection: 
            // hide chain selection if it's disabled
            payOptions.buyWithCrypto !== false &&
                payOptions.buyWithCrypto?.prefillSource?.allowEdits?.chain !== false
                ? {
                    chains: supportedSourcesQuery.data.map((x) => x.chain),
                    select: (c) => setFromChain(c),
                }
                : undefined, connectLocale: connectLocale, client: client, modalTitle: "Pay with" }));
    }
    return (_jsx(Container, { animate: "fadein", children: _jsxs("div", { children: [screen.id === "main" && (_jsx(MainScreen, { title: props.title, payerAccount: payer?.account, client: client, onSelectBuyToken: () => setScreen({ id: "select-to-token", backScreen: screen }), payOptions: payOptions, setTokenAmount: setTokenAmount, setToChain: setToChain, setToToken: setToToken, setFromChain: setFromChain, setFromToken: setFromToken, toChain: toChain, toToken: toToken, tokenAmount: tokenAmount, connectOptions: props.connectOptions, setScreen: setScreen, supportedDestinations: supportedDestinations, onBack: props.onBack, theme: props.theme, hasEditedAmount: hasEditedAmount, setHasEditedAmount: setHasEditedAmount, enabledPaymentMethods: enabledPaymentMethods })), (screen.id === "select-payment-method" ||
                    screen.id === "select-wallet" ||
                    screen.id === "buy-with-crypto" ||
                    screen.id === "buy-with-fiat") &&
                    payer && (_jsxs(TokenSelectedLayout, { title: props.title, selectedChain: toChain, selectedToken: toToken, tokenAmount: tokenAmount, client: client, onBack: () => {
                        if (enabledPaymentMethods.showPaymentSelection &&
                            (screen.id === "select-wallet" ||
                                screen.id === "buy-with-fiat")) {
                            setScreen({ id: "select-payment-method" });
                        }
                        else if (screen.id === "buy-with-crypto") {
                            setScreen({ id: "select-wallet" });
                        }
                        else {
                            setScreen({ id: "main" });
                        }
                    }, children: [screen.id === "select-payment-method" && (_jsx(PaymentMethodSelection, { setScreen: (id) => setScreen({ id }) })), screen.id === "select-wallet" && (_jsx(WalletSwitcherDrawerContent, { client: client, onSelect: (w) => {
                                const chain = w.getChain();
                                const account = w.getAccount();
                                if (chain && account) {
                                    setPayer({
                                        account,
                                        chain,
                                        wallet: w,
                                    });
                                    setScreen({ id: "buy-with-crypto" });
                                }
                            }, showAllWallets: !!props.connectOptions?.showAllWallets, wallets: props.connectOptions?.wallets, onBack: () => {
                                // no-op
                            }, onConnect: () => {
                                setScreen({
                                    id: "connect-payer-wallet",
                                    backScreen: {
                                        id: "select-wallet",
                                    },
                                });
                            }, selectedAddress: payer.account.address })), screen.id === "buy-with-crypto" && activeAccount && (_jsx(SwapScreenContent, { setScreen: setScreen, tokenAmount: deferredTokenAmount, toChain: toChain, toToken: toToken, fromChain: fromChain, fromToken: fromToken, showFromTokenSelector: () => {
                                setScreen({
                                    id: "select-from-token",
                                    backScreen: screen,
                                });
                            }, payer: payer, client: client, isEmbed: props.isEmbed, onDone: onDone, payOptions: payOptions, connectLocale: connectLocale, connectOptions: props.connectOptions, setPayer: setPayer, 
                            // pass it even though we are passing payer, because payer might be different
                            activeAccount: activeAccount, setTokenAmount: setTokenAmount, setHasEditedAmount: setHasEditedAmount, disableTokenSelection: payDisabled === true ||
                                (payOptions.buyWithCrypto !== false &&
                                    payOptions.buyWithCrypto?.prefillSource?.allowEdits
                                        ?.chain === false &&
                                    payOptions.buyWithCrypto?.prefillSource?.allowEdits
                                        ?.token === false) })), screen.id === "buy-with-fiat" && (_jsx(FiatScreenContent, { setScreen: setScreen, tokenAmount: deferredTokenAmount, toChain: toChain, toToken: toToken, selectedCurrency: selectedCurrency, client: client, isEmbed: props.isEmbed, onDone: onDone, payOptions: payOptions, theme: props.theme, showCurrencySelector: () => {
                                setScreen({
                                    id: "select-currency",
                                    backScreen: screen,
                                });
                            }, payer: payer, setTokenAmount: setTokenAmount, setHasEditedAmount: setHasEditedAmount }))] }))] }) }));
}
function SelectedTokenInfo(props) {
    return (_jsx("div", { children: _jsxs(Container, { flex: "row", gap: "sm", center: "y", style: {
                justifyContent: "space-between",
            }, children: [_jsxs(Container, { flex: "row", gap: "xs", center: "y", children: [_jsx(Text, { color: "primaryText", "data-testid": "tokenAmount", size: "xl", children: formatNumber(Number(props.tokenAmount), 6) }), _jsxs(Container, { flex: "row", gap: "xxs", center: "y", children: [_jsx(TokenSymbol, { token: props.selectedToken, chain: props.selectedChain, size: "md", color: "secondaryText" }), _jsx(PayTokenIcon, { chain: props.selectedChain, client: props.client, size: "sm", token: props.selectedToken })] })] }), _jsx(ChainName, { chain: props.selectedChain, client: props.client, size: "sm", short: true })] }) }));
}
function MainScreen(props) {
    const { setTokenAmount, setToChain, setToToken, setFromChain, setFromToken, payerAccount, client, tokenAmount, payOptions, toToken, toChain, supportedDestinations, enabledPaymentMethods, } = props;
    const { showPaymentSelection, buyWithCryptoEnabled, buyWithFiatEnabled } = enabledPaymentMethods;
    const disableContinue = !tokenAmount;
    switch (payOptions.mode) {
        case "transaction": {
            return (_jsx(TransactionModeScreen, { supportedDestinations: supportedDestinations, payUiOptions: payOptions, payerAccount: payerAccount, connectOptions: props.connectOptions, client: client, onContinue: (tokenAmount, toChain, toToken) => {
                    setTokenAmount(tokenAmount);
                    setToChain(toChain);
                    setFromChain(toChain);
                    setFromToken(toToken);
                    setToToken(toToken);
                    if (showPaymentSelection) {
                        props.setScreen({ id: "select-payment-method" });
                    }
                    else if (buyWithCryptoEnabled) {
                        props.setScreen({ id: "select-wallet" });
                    }
                    else if (buyWithFiatEnabled) {
                        props.setScreen({ id: "buy-with-fiat" });
                    }
                    else {
                        // default to buy with crypto with connected wallet if chain not supported by pay
                        props.setScreen({ id: "select-wallet" });
                    }
                } }));
        }
        case "direct_payment": {
            return (_jsx(DirectPaymentModeScreen, { client: client, payUiOptions: payOptions, payerAccount: payerAccount, connectOptions: props.connectOptions, supportedDestinations: supportedDestinations, onContinue: (tokenAmount, toChain, toToken) => {
                    setTokenAmount(tokenAmount);
                    setToChain(toChain);
                    setFromChain(toChain);
                    setFromToken(toToken);
                    setToToken(toToken);
                    if (showPaymentSelection) {
                        props.setScreen({ id: "select-payment-method" });
                    }
                    else if (buyWithCryptoEnabled) {
                        props.setScreen({ id: "buy-with-crypto" });
                    }
                    else if (buyWithFiatEnabled) {
                        props.setScreen({ id: "buy-with-fiat" });
                    }
                    else {
                        // default to buy with crypto with connected wallet if chain not supported by pay
                        props.setScreen({ id: "select-wallet" });
                    }
                } }));
        }
        default: {
            return (_jsxs(Container, { p: "lg", children: [_jsx(ModalHeader, { title: props.title, onBack: props.onBack }), _jsx(Spacer, { y: "xl" }), _jsx(BuyTokenInput, { value: tokenAmount, onChange: async (value) => {
                            props.setHasEditedAmount(true);
                            setTokenAmount(value);
                        }, freezeAmount: payOptions.prefillBuy?.allowEdits?.amount === false, freezeChainAndToken: payOptions.prefillBuy?.allowEdits?.chain === false &&
                            payOptions.prefillBuy?.allowEdits?.token === false, token: toToken, chain: toChain, onSelectToken: props.onSelectBuyToken, client: props.client }), _jsx(Spacer, { y: "xl" }), _jsx(Container, { flex: "column", gap: "sm", children: !payerAccount ? (_jsx("div", { children: _jsx(ConnectButton, { ...props.connectOptions, client: props.client, theme: props.theme, connectButton: {
                                    style: {
                                        width: "100%",
                                    },
                                } }) })) : (_jsx(Button, { variant: "accent", fullWidth: true, disabled: disableContinue, "data-disabled": disableContinue, onClick: () => {
                                if (showPaymentSelection) {
                                    props.setScreen({ id: "select-payment-method" });
                                }
                                else if (buyWithCryptoEnabled) {
                                    props.setScreen({ id: "buy-with-crypto" });
                                }
                                else if (buyWithFiatEnabled) {
                                    props.setScreen({ id: "buy-with-fiat" });
                                }
                                else {
                                    console.error("No payment method enabled");
                                }
                            }, children: "Continue" })) })] }));
        }
    }
}
function TokenSelectedLayout(props) {
    return (_jsxs(Container, { children: [_jsx(Container, { p: "lg", children: _jsx(ModalHeader, { title: props.title, onBack: props.onBack }) }), _jsxs(Container, { px: "lg", style: {
                    paddingBottom: spacing.lg,
                }, children: [_jsx(Spacer, { y: "xs" }), _jsx(SelectedTokenInfo, { selectedToken: props.selectedToken, selectedChain: props.selectedChain, tokenAmount: props.tokenAmount, client: props.client }), _jsx(Spacer, { y: "md" }), _jsx(Line, {}), _jsx(Spacer, { y: "lg" }), _jsx(Text, { size: "sm", children: " Pay with " }), _jsx(Spacer, { y: "sm" }), props.children] })] }));
}
function PaymentMethodSelection(props) {
    return (_jsx(Container, { animate: "fadein", children: _jsxs(Container, { flex: "column", gap: "sm", children: [_jsxs(Button, { variant: "outline", bg: "tertiaryBg", onClick: () => props.setScreen("buy-with-fiat"), gap: "sm", style: {
                        justifyContent: "flex-start",
                        textAlign: "left",
                    }, children: [_jsx(Container, { color: "secondaryText", flex: "row", center: "both", children: _jsx(IdCardIcon, { style: {
                                    width: iconSize.md,
                                    height: iconSize.md,
                                } }) }), _jsxs(Container, { flex: "column", gap: "xxs", children: [_jsx(Text, { size: "md", color: "primaryText", children: "Credit Card" }), _jsx(Text, { size: "xs", children: "Securely pay with credit card" })] })] }), _jsxs(Button, { variant: "outline", bg: "tertiaryBg", onClick: () => props.setScreen("select-wallet"), style: {
                        justifyContent: "flex-start",
                    }, gap: "sm", children: [_jsx(Container, { color: "secondaryText", flex: "row", center: "both", children: _jsx(CoinsIcon, { size: iconSize.md }) }), _jsxs(Container, { flex: "column", gap: "xxs", children: [_jsx(Text, { size: "md", color: "primaryText", children: "Crypto" }), _jsx(Text, { size: "xs", children: "Pay with your connected wallet" })] })] })] }) }));
}
function SwapScreenContent(props) {
    const { setScreen, payer, client, toChain, tokenAmount, toToken, fromChain, fromToken, showFromTokenSelector, payOptions, disableTokenSelection, } = props;
    const defaultRecipientAddress = props.payOptions?.paymentInfo?.sellerAddress;
    const receiverAddress = defaultRecipientAddress || props.activeAccount.address;
    const { drawerRef, drawerOverlayRef, isOpen, setIsOpen } = useDrawer();
    const [drawerScreen, setDrawerScreen] = useState("fees");
    const fromTokenBalanceQuery = useWalletBalance({
        address: payer.account.address,
        chain: fromChain,
        tokenAddress: isNativeToken(fromToken) ? undefined : fromToken.address,
        client,
    });
    const fromTokenId = isNativeToken(fromToken)
        ? NATIVE_TOKEN_ADDRESS
        : fromToken.address.toLowerCase();
    const toTokenId = isNativeToken(toToken)
        ? NATIVE_TOKEN_ADDRESS
        : toToken.address.toLowerCase();
    const swapRequired = !!tokenAmount &&
        !(fromChain.id === toChain.id && fromTokenId === toTokenId);
    const quoteParams = swapRequired
        ? {
            // wallets
            fromAddress: payer.account.address,
            toAddress: receiverAddress,
            // from
            fromChainId: fromChain.id,
            fromTokenAddress: isNativeToken(fromToken)
                ? NATIVE_TOKEN_ADDRESS
                : fromToken.address,
            // to
            toChainId: toChain.id,
            toTokenAddress: isNativeToken(toToken)
                ? NATIVE_TOKEN_ADDRESS
                : toToken.address,
            toAmount: tokenAmount,
            client,
            purchaseData: payOptions.purchaseData,
        }
        : undefined;
    const quoteQuery = useBuyWithCryptoQuote(quoteParams, {
        // refetch every 30 seconds
        staleTime: 30 * 1000,
        refetchInterval: 30 * 1000,
        gcTime: 30 * 1000,
    });
    const sourceTokenAmount = swapRequired
        ? quoteQuery.data?.swapDetails.fromAmount
        : tokenAmount;
    const isNotEnoughBalance = !!sourceTokenAmount &&
        !!fromTokenBalanceQuery.data &&
        Number(fromTokenBalanceQuery.data.displayValue) < Number(sourceTokenAmount);
    const disableContinue = (swapRequired && !quoteQuery.data) || isNotEnoughBalance;
    const switchChainRequired = props.payer.chain.id !== fromChain.id;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    function getErrorMessage(err) {
        const defaultMessage = "Unable to get price quote";
        try {
            if (err.error.code === "MINIMUM_PURCHASE_AMOUNT") {
                const obj = err.error;
                const minAmountToken = obj.data.minimumAmountEth;
                return {
                    minAmount: formatNumber(Number(minAmountToken), 6),
                };
            }
        }
        catch { }
        return {
            msg: [defaultMessage],
        };
    }
    const errorMsg = !quoteQuery.isLoading && quoteQuery.error
        ? getErrorMessage(quoteQuery.error)
        : undefined;
    function showSwapFlow() {
        if (props.payOptions.mode === "direct_payment" &&
            !isNotEnoughBalance &&
            !swapRequired) {
            // same currency, just direct transfer
            setScreen({
                id: "transfer-flow",
            });
        }
        else if (props.payOptions.mode === "transaction" &&
            !isNotEnoughBalance &&
            !swapRequired) {
            if (payer.account.address !== receiverAddress) {
                // needs transfer from another wallet before executing the transaction
                setScreen({
                    id: "transfer-flow",
                });
            }
            else {
                // has enough balance to just do the transaction directly
                props.onDone();
            }
            return;
        }
        if (!quoteQuery.data) {
            return;
        }
        setScreen({
            id: "swap-flow",
            quote: quoteQuery.data,
        });
    }
    function showFees() {
        if (!quoteQuery.data) {
            return;
        }
        setIsOpen(true);
        setDrawerScreen("fees");
    }
    return (_jsxs(Container, { flex: "column", gap: "md", animate: "fadein", children: [isOpen && (_jsxs(_Fragment, { children: [_jsx(DrawerOverlay, { ref: drawerOverlayRef }), _jsx(Drawer, { ref: drawerRef, close: () => setIsOpen(false), children: drawerScreen === "fees" && quoteQuery.data && (_jsxs("div", { children: [_jsx(Text, { size: "lg", color: "primaryText", children: "Fees" }), _jsx(Spacer, { y: "lg" }), _jsx(SwapFees, { quote: quoteQuery.data })] })) })] })), _jsxs("div", { children: [_jsx(WalletSelectorButton, { client: props.client, onClick: () => {
                            setScreen({ id: "select-wallet" });
                        }, address: props.payer.account.address, walletId: props.payer.wallet.id, containerStyle: {
                            borderBottomRightRadius: 0,
                            borderBottomLeftRadius: 0,
                        } }), _jsx(PayWithCrypto, { value: sourceTokenAmount || "", onSelectToken: showFromTokenSelector, chain: fromChain, token: fromToken, isLoading: quoteQuery.isLoading && !sourceTokenAmount, client: client, freezeChainAndTokenSelection: disableTokenSelection, payerAccount: props.payer.account, swapRequired: swapRequired }), swapRequired && (_jsx(EstimatedTimeAndFees, { quoteIsLoading: quoteQuery.isLoading, estimatedSeconds: quoteQuery.data?.swapDetails.estimated.durationSeconds, onViewFees: showFees })), _jsx(Spacer, { y: "md" })] }), errorMsg && (_jsxs("div", { children: [errorMsg.minAmount && (_jsxs(Text, { color: "danger", size: "sm", center: true, multiline: true, children: ["Minimum amount is ", errorMsg.minAmount, " ", _jsx(TokenSymbol, { token: toToken, chain: toChain, size: "sm", inline: true, color: "danger" })] })), errorMsg.msg?.map((msg) => (_jsx(Text, { color: "danger", size: "sm", center: true, multiline: true, children: msg }, msg)))] })), !errorMsg && isNotEnoughBalance && (_jsxs("div", { children: [_jsx(Text, { color: "danger", size: "sm", center: true, multiline: true, children: "Not enough funds." }), _jsx(Text, { color: "danger", size: "sm", center: true, multiline: true, children: "Try a different wallet or token." })] })), errorMsg?.minAmount ? (_jsx(Button, { variant: "accent", fullWidth: true, onClick: () => {
                    props.setTokenAmount(String(errorMsg.minAmount));
                    props.setHasEditedAmount(true);
                }, children: "Set Minimum" })) : switchChainRequired &&
                !quoteQuery.isLoading &&
                !isNotEnoughBalance &&
                !quoteQuery.error ? (_jsx(SwitchNetworkButton, { variant: "accent", fullWidth: true, switchChain: async () => {
                    await props.payer.wallet.switchChain(fromChain);
                } })) : (_jsx(Button, { variant: disableContinue ? "outline" : "accent", fullWidth: true, "data-disabled": disableContinue, disabled: disableContinue, onClick: async () => {
                    if (!disableContinue) {
                        showSwapFlow();
                    }
                }, gap: "xs", children: quoteQuery.isLoading ? (_jsxs(_Fragment, { children: ["Getting price quote", _jsx(Spinner, { size: "sm", color: "accentText" })] })) : ("Continue") }))] }));
}
function FiatScreenContent(props) {
    const { toToken, tokenAmount, payer, client, setScreen, toChain, showCurrencySelector, selectedCurrency, } = props;
    const defaultRecipientAddress = props.payOptions?.paymentInfo?.sellerAddress;
    const receiverAddress = defaultRecipientAddress || props.payer.account.address;
    const { drawerRef, drawerOverlayRef, isOpen, setIsOpen } = useDrawer();
    const [drawerScreen, setDrawerScreen] = useState("fees");
    const buyWithFiatOptions = props.payOptions.buyWithFiat;
    const fiatQuoteQuery = useBuyWithFiatQuote(buyWithFiatOptions !== false && tokenAmount
        ? {
            fromCurrencySymbol: selectedCurrency.shorthand,
            toChainId: toChain.id,
            toAddress: receiverAddress,
            toTokenAddress: isNativeToken(toToken)
                ? NATIVE_TOKEN_ADDRESS
                : toToken.address,
            toAmount: tokenAmount,
            client,
            isTestMode: buyWithFiatOptions?.testMode,
            purchaseData: props.payOptions.purchaseData,
            fromAddress: payer.account.address,
        }
        : undefined);
    function handleSubmit() {
        if (!fiatQuoteQuery.data) {
            return;
        }
        const hasTwoSteps = isSwapRequiredPostOnramp(fiatQuoteQuery.data);
        let openedWindow = null;
        if (!hasTwoSteps) {
            openedWindow = openOnrampPopup(fiatQuoteQuery.data.onRampLink, typeof props.theme === "string" ? props.theme : props.theme.type);
            addPendingTx({
                type: "fiat",
                intentId: fiatQuoteQuery.data.intentId,
            });
        }
        setScreen({
            id: "fiat-flow",
            quote: fiatQuoteQuery.data,
            openedWindow,
        });
    }
    function showFees() {
        if (!fiatQuoteQuery.data) {
            return;
        }
        setDrawerScreen("fees");
        setIsOpen(true);
    }
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    function getErrorMessage(err) {
        const defaultMessage = "Unable to get price quote";
        try {
            if (err.error.code === "MINIMUM_PURCHASE_AMOUNT") {
                const obj = err.error;
                const minAmountToken = obj.data.minimumAmountEth;
                return {
                    minAmount: formatNumber(Number(minAmountToken), 6),
                };
            }
        }
        catch { }
        return {
            msg: [defaultMessage],
        };
    }
    const disableSubmit = !fiatQuoteQuery.data;
    const errorMsg = !fiatQuoteQuery.isLoading && fiatQuoteQuery.error
        ? getErrorMessage(fiatQuoteQuery.error)
        : undefined;
    return (_jsxs(Container, { flex: "column", gap: "md", animate: "fadein", children: [isOpen && (_jsxs(_Fragment, { children: [_jsx(DrawerOverlay, { ref: drawerOverlayRef }), _jsx(Drawer, { ref: drawerRef, close: () => setIsOpen(false), children: drawerScreen === "fees" && fiatQuoteQuery.data && (_jsxs("div", { children: [_jsx(Text, { size: "lg", color: "primaryText", children: "Fees" }), _jsx(Spacer, { y: "lg" }), _jsx(FiatFees, { quote: fiatQuoteQuery.data })] })) })] })), _jsxs("div", { children: [_jsx(PayWithCreditCard, { isLoading: fiatQuoteQuery.isLoading, value: fiatQuoteQuery.data?.fromCurrencyWithFees.amount, client: client, currency: selectedCurrency, onSelectCurrency: showCurrencySelector }), _jsx(EstimatedTimeAndFees, { quoteIsLoading: fiatQuoteQuery.isLoading, estimatedSeconds: fiatQuoteQuery.data?.estimatedDurationSeconds, onViewFees: showFees }), _jsx(Spacer, { y: "md" })] }), errorMsg && (_jsxs("div", { children: [errorMsg.minAmount && (_jsxs(Text, { color: "danger", size: "sm", center: true, multiline: true, children: ["Minimum amount is ", errorMsg.minAmount, " ", _jsx(TokenSymbol, { token: toToken, chain: toChain, size: "sm", inline: true, color: "danger" })] })), errorMsg.msg?.map((msg) => (_jsx(Text, { color: "danger", size: "sm", center: true, multiline: true, children: msg }, msg)))] })), errorMsg?.minAmount ? (_jsx(Button, { variant: "accent", fullWidth: true, onClick: () => {
                    props.setTokenAmount(String(errorMsg.minAmount));
                    props.setHasEditedAmount(true);
                }, children: "Set Minimum" })) : (_jsx(Button, { variant: disableSubmit ? "outline" : "accent", "data-disabled": disableSubmit, disabled: disableSubmit, fullWidth: true, onClick: handleSubmit, gap: "xs", children: fiatQuoteQuery.isLoading ? (_jsxs(_Fragment, { children: ["Getting price quote", _jsx(Spinner, { size: "sm", color: "accentText" })] })) : ("Continue") }))] }));
}
function createSupportedTokens(data, payOptions, supportedTokensOverrides) {
    const tokens = {};
    const isBuyWithFiatDisabled = payOptions.buyWithFiat === false;
    const isBuyWithCryptoDisabled = payOptions.buyWithCrypto === false;
    // FIXME (pay) when buywithFiat is disabled, missing a bunch of tokens on base??
    for (const x of data) {
        tokens[x.chain.id] = x.tokens.filter((t) => {
            // it token supports both - include it
            if (t.buyWithCryptoEnabled && t.buyWithFiatEnabled) {
                return true;
            }
            // if buyWithFiat is disabled, and buyWithCrypto is not supported by token - exclude the token
            if (!t.buyWithCryptoEnabled && isBuyWithFiatDisabled) {
                return false;
            }
            // if buyWithCrypto is disabled, and buyWithFiat is not supported by token - exclude the token
            if (!t.buyWithFiatEnabled && isBuyWithCryptoDisabled) {
                return false;
            }
            return true; // include the token
        });
    }
    // override with props.supportedTokens
    if (supportedTokensOverrides) {
        for (const k in supportedTokensOverrides) {
            const key = Number(k);
            const tokenList = supportedTokensOverrides[key];
            if (tokenList) {
                tokens[key] = tokenList;
            }
        }
    }
    return tokens;
}
function ChainSelectionScreen(props) {
    return (_jsx(NetworkSelectorContent, { client: props.client, connectLocale: props.connectLocale, showTabs: false, onBack: props.goBack, chains: props.chains, closeModal: props.goBack, networkSelector: {
            renderChain(renderChainProps) {
                return (_jsx(ChainButton, { chain: renderChainProps.chain, confirming: false, switchingFailed: false, onClick: () => {
                        props.setChain(renderChainProps.chain);
                        props.goBack();
                    }, client: props.client, connectLocale: props.connectLocale }));
            },
        } }));
}
//# sourceMappingURL=BuyScreen.js.map